import { useState } from "react";
import { submitBOQ } from "../lib/api";
import { Upload, CheckCircle2, FileText, Phone, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function BOQPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("message", form.message);
      if (file) fd.append("file", file);
      await submitBOQ(fd);
      setDone(true);
      toast.success("Quotation request received!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="boq-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-slate-900">
          Thank you, {form.name.split(" ")[0]}!
        </h1>
        <p className="mt-3 text-slate-600">
          Your quotation request has been received. Our team will get back to
          you on{" "}
          <span className="font-medium text-slate-900">{form.email}</span> within
          a few hours.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href="https://wa.me/917400042543"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-amber-500 text-slate-900 rounded-full font-medium hover:bg-amber-400"
          >
            WhatsApp us now
          </a>
          <a
            href="/"
            className="px-6 py-3 bg-white border border-slate-200 rounded-full font-medium hover:bg-slate-100"
          >
            Back to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600 mb-4">
              Bill of Quantities
            </p>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
              Send your BOQ.<br />
              Get a same-day quote.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Upload a PDF or photo of your requirement list. Our team will
              respond with brand-wise pricing, availability and delivery
              timelines.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-amber-600" />
                </span>
                <div>
                  <p className="font-medium text-slate-900">WhatsApp directly</p>
                  <a
                    href="https://wa.me/917400042543"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-500 hover:text-amber-600"
                  >
                    +91 74000 42543
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-amber-600" />
                </span>
                <div>
                  <p className="font-medium text-slate-900">Email us</p>
                  <a
                    href="mailto:inquiry@veerelectrical.com"
                    className="text-sm text-slate-500 hover:text-amber-600"
                  >
                    inquiry@veerelectrical.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-amber-600" />
                </span>
                <div>
                  <p className="font-medium text-slate-900">Call</p>
                  <a
                    href="tel:+917400042543"
                    className="text-sm text-slate-500 hover:text-amber-600"
                  >
                    +91 74000 42543
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-white rounded-3xl border border-slate-200 p-8 lg:p-10 shadow-sm"
              data-testid="boq-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                    Full name
                  </label>
                  <input
                    value={form.name}
                    onChange={handle("name")}
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-colors"
                    data-testid="boq-name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                    Phone
                  </label>
                  <input
                    value={form.phone}
                    onChange={handle("phone")}
                    type="tel"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
                    data-testid="boq-phone"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                  Email
                </label>
                <input
                  value={form.email}
                  onChange={handle("email")}
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
                  data-testid="boq-email"
                />
              </div>
              <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                  Message <span className="text-slate-300">(optional)</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={handle("message")}
                  rows={4}
                  placeholder="Tell us about your project — site address, timeline, brand preferences..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none resize-none"
                  data-testid="boq-message"
                />
              </div>
              <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">
                  BOQ file (PDF / JPG)
                </label>
                <label
                  className="flex items-center justify-center gap-3 w-full px-4 py-8 rounded-xl border-2 border-dashed border-slate-300 hover:border-amber-500 hover:bg-amber-50/30 cursor-pointer transition-colors"
                  data-testid="boq-file-label"
                >
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="boq-file"
                  />
                  {file ? (
                    <span className="flex items-center gap-2 text-slate-900">
                      <FileText className="w-5 h-5 text-amber-600" />
                      {file.name}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-slate-500">
                      <Upload className="w-5 h-5" />
                      Click to upload PDF or photo of your BOQ
                    </span>
                  )}
                </label>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-7 w-full px-6 py-4 bg-slate-900 text-white rounded-full font-medium hover:bg-amber-500 hover:text-slate-900 transition-colors disabled:opacity-50"
                data-testid="boq-submit"
              >
                {submitting ? "Sending…" : "Send for quotation"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
